import { EmptyState, NoFilteredResults } from '@components/EmptyState';
import {
	type ColumnProps,
	DropdownPositions,
	FileIcons,
	Loader,
	Pagination,
	Table,
} from '@gurupay/gp-ui-library';
import {
	type StatementFiltersProps,
	useAllRequestedStatements,
} from '@hooks/useAllRequestedStatements';
import { useClearFilter } from '@hooks/useClearFilter.ts';
import { usePagination } from '@hooks/usePagination';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import StatementsFilters from './StatementsFilters';
import { StatementsRenderers } from './StatementsRenderers';

export const DEFAULT_FILTER_VALUES = {
	iban: '',
	type: [],
};

const DownloadStatementsView = () => {
	const isMobile = useMediaQuery({ query: '(max-width: 480px)' });
	const {
		currentPage,
		setCurrentPage,
		pageSize,
		pageOptions,
		handlePageSizeChange,
		pageText,
		totalCountText,
	} = usePagination();
	const [filters, setFilters] = useState<StatementFiltersProps>(DEFAULT_FILTER_VALUES);
	const { data, isLoading } = useAllRequestedStatements({
		currentPage,
		limit: pageSize,
		filters,
	});
	const filterForm = useForm<StatementFiltersProps>();
	const handleFilterApply = (filterOptions: StatementFiltersProps) => {
		setFilters(filterOptions);
		setCurrentPage(1);
	};
	const { hasFilters, handleClear } = useClearFilter<StatementFiltersProps>(
		filterForm,
		handleFilterApply,
	);
	const { t } = useTranslation('', { keyPrefix: 'statements' });
	const columns: ColumnProps[] = isMobile
		? [{ label: '', key: 'downloadStatements', align: 'left' }]
		: [
				{ label: t('accountName'), key: 'accountName', align: 'left' },
				{ label: t('period'), key: 'period', align: 'left', minWidth: '190px' },
				{ label: t('fileFormat'), key: 'fileType', align: 'left' },
				{ label: t('status'), key: 'status', align: 'left' },
				{ label: t('date'), key: 'date' },
				{ label: '', key: 'download', width: '36px' },
			];

	return (
		<div className="mt-space-24">
			<FormProvider {...filterForm}>
				<StatementsFilters onApply={handleFilterApply} />
			</FormProvider>
			{isLoading && (
				<div className="mt-space-40 m-auto text-center">
					<Loader />
				</div>
			)}
			{Boolean(data?.items?.length) && (
				<Table
					className="mt-space-40 max-mobile:mt-space-24"
					columns={columns}
					renders={StatementsRenderers(t)}
					rows={data?.items ?? []}
					showHeader={!isMobile}
				/>
			)}
			{!data?.items?.length &&
				!isLoading &&
				(hasFilters ? (
					<NoFilteredResults onClear={handleClear} />
				) : (
					<EmptyState
						icon={<FileIcons />}
						subTitle={t('noDataDescription')}
						title={t('noData')}
					/>
				))}

			{data?.pagination && data.pagination.count > pageSize && (
				<Pagination
					className="mt-space-32!"
					currentPage={currentPage}
					dropdownOpenPosition={DropdownPositions.TOP}
					onPageChange={setCurrentPage}
					onPageSizeChange={handlePageSizeChange}
					pageOptions={pageOptions}
					pageSize={pageSize}
					pageText={pageText}
					totalCount={data.pagination.count}
					totalCountText={totalCountText}
				/>
			)}
		</div>
	);
};

export default DownloadStatementsView;
